/**
 * Consulting & Strategy vertical — the third Extern Labs sprint, authored directly against the
 * {@link VerticalPack} contract (like data-analytics; finance remains an adapter over its shipped
 * `_data` layer).
 *
 * THE AXIS: the two worlds a consulting career splits into.
 *   pole a — Strategy / advisory: work out what the business should do next, size it, and make a
 *            senior room believe it.
 *   pole b — Operations / execution: make the business actually work — the process, the rollout,
 *            the fix that survives contact with a real team.
 *
 * THE ARTIFACT. Finance's artifact is an income statement; data's is a product funnel. This
 * vertical's is an OPERATING TABLE — the handful of numbers a business runs on (demand, quality,
 * friction, failure), read for whether the operation is keeping up with its own growth. It is the
 * first thing any consultant is handed, it is readable without domain training, and — unlike the
 * other two artifacts — its rows are independent gauges rather than terms in an equation or
 * survivors of a step. (See `consulting-copy.ts` for why the primer's operators are empty.)
 *
 * ⚠️ M4 NUMBERS. Every figure in `M4_SUBJECTS` is a real, publicly reported metric from the
 * company's own 10-K, SEC-filed press release or shareholder letter (verified 2026-08-06). Nothing
 * is interpolated, smoothed or invented. Notes the reader should know about:
 *
 *   1. ROWS ARE PER-SUBJECT, in each company's own reporting vocabulary, and each subject's rows
 *      share ONE unit (the table renders a single unit per subject). Airbnb is the visible
 *      consequence: its active-listings count is only ever reported rounded ("over 8 million"), so
 *      its second row is Gross Booking Value + Revenue instead — which happens to teach the best
 *      lesson in the set (the take rate).
 *   2. THE EFFICIENCY READ is always a deeper tier over the tier above it — Chipotlanes inside
 *      restaurants, trips per consumer, revenue inside GBV. Costco's is the one exception: it is
 *      graded on executive members inside PAID members (row 3 over row 2), because cardholders is
 *      the audience row and the trade-up story lives one tier down.
 *   3. Costco's fiscal year ends in Aug/Sep; the label "FY2022" is Costco's own FY2022, not the
 *      calendar year. Chipotle's 2024 restaurant count (3,726) is company-owned; its first 3
 *      licensed international stores are reported separately. Chipotle's 2023 Chipotlane total
 *      includes conversions of existing restaurants (the company's own cumulative figure).
 */

import { Compass } from 'lucide-react';

import { CONSULTING_DISCOVERY_EVENTS } from '@/lib/analytics/consulting-discovery-events';

import type {
  LabsAnswerKey,
  LabsArchetype,
  LabsAxis,
  LabsChipGroup,
  LabsDerivedMetric,
  LabsMindsetQuestion,
  LabsRecommendation,
  LabsSubject,
  LabsSwipeCard,
  VerticalPack,
} from '../_engine/types';
import { CONSULTING_COPY } from './consulting-copy';

// ---------------------------------------------------------------------------
// The axis
// ---------------------------------------------------------------------------

/**
 * Burnt sienna for the advisory side, cobalt for the execution side, and the same gold finance and
 * data use for the learner fluent in both — the "both poles" accent is deliberately shared across
 * verticals so a POLYMATH reveal reads as the same outcome wherever a learner meets it.
 */
const CONSULTING_AXIS: LabsAxis = {
  bothAccent: '#B8860B',
  poles: {
    // A TEMPERAMENT split, not a job-title split: pole A's output is a recommendation a decision-maker
    // acts on, pole B's output is an operation that runs better after you leave. Labelled by job family
    // ("Management Consulting" vs "Operations Management") it would exclude the strategy, research and
    // process work we actually teach.
    a: { accent: '#9A3412', label: 'Strategy' },
    b: { accent: '#1E40AF', label: 'Operations' },
  },
};

// ---------------------------------------------------------------------------
// Archetypes
// ---------------------------------------------------------------------------

const CONSULTING_ARCHETYPES: Readonly<Record<string, LabsArchetype>> = {
  gen: {
    accent: CONSULTING_AXIS.bothAccent,
    body: 'You can find the answer and then make it happen. That pairing has a name — chief of staff — and it is one of the highest-leverage seats in any business, precisely because so few people can do both halves.',
    displayName: 'THE POLYMATH',
    id: 'gen',
    tagline: 'Fluent in both halves of the job.',
  },
  operator: {
    accent: CONSULTING_AXIS.poles.b.accent,
    body: "You're drawn to the work itself: the process that breaks under growth, the rollout that sticks, the fix that still holds after you leave the room. The operations, people-analytics and process-design tracks are your home.",
    displayName: 'THE OPERATOR',
    id: 'operator',
    tagline: "You'd rather fix the machine than describe it.",
  },
  strategist: {
    accent: CONSULTING_AXIS.poles.a.accent,
    body: "You're drawn to the question behind the business: what should it do next, and what would make a smart room believe it. The strategy, consumer-research and market-analysis tracks are your home.",
    displayName: 'THE STRATEGIST',
    id: 'strategist',
    tagline: 'Turns a messy question into a decision someone can act on.',
  },
};

// ---------------------------------------------------------------------------
// M2 scoring
// ---------------------------------------------------------------------------

/** The seven slider stops are 0–6. Stop 3 is the exact centre: it leans to NEITHER pole. */
const SLIDER_CENTRE = 3;

/**
 * Score the four M2 sliders by TALLYING BOTH POLES against each other — the data-analytics scoring,
 * copied deliberately (see `data-analytics.ts` for the full account of why finance's one-sided count
 * is wrong and why it is not being fixed there mid-experiment). A centre answer counts for nobody:
 *   more Strategy answers   → THE STRATEGIST
 *   more Operations answers → THE OPERATOR
 *   a tie (including all-centre, and including a never-touched quiz) → THE POLYMATH
 */
function deriveArchetype(answers: Readonly<Record<string, number>>): string {
  let strategy = 0;
  let operations = 0;

  for (const value of Object.values(answers)) {
    if (value < SLIDER_CENTRE) {
      strategy += 1;
    } else if (value > SLIDER_CENTRE) {
      operations += 1;
    }
  }

  if (strategy === operations) {
    return 'gen';
  }
  return strategy > operations ? 'strategist' : 'operator';
}

// ---------------------------------------------------------------------------
// M1 — the swipe deck
// ---------------------------------------------------------------------------

/**
 * Eight activities, alternating poles (a, b, a, b, …) so the deck never lets a learner straight-swipe
 * one side. Each one is a concrete thing a consultant does on an ordinary Tuesday. The pole is hidden
 * from the learner; it only drives the advisory M1 lean.
 */
const M1_CARDS: readonly LabsSwipeCard[] = [
  { icon: '🧭', id: 'm1-1', pole: 'a', text: 'Size a market no one has clean numbers for' },
  { icon: '🔧', id: 'm1-2', pole: 'b', text: 'Map the process behind a late order and find where it breaks' },
  { icon: '🎤', id: 'm1-3', pole: 'a', text: 'Interview a customer and find the reason they almost left' },
  { icon: '📋', id: 'm1-4', pole: 'b', text: 'Redesign the workflow so the team stops doing the work twice' },
  { icon: '📊', id: 'm1-5', pole: 'a', text: 'Build the deck that convinces the CEO to kill a product' },
  { icon: '⏱️', id: 'm1-6', pole: 'b', text: 'Cut the meeting that burns 40 team-hours a week' },
  { icon: '🔍', id: 'm1-7', pole: 'a', text: 'Find the one number that changes the recommendation' },
  { icon: '🚀', id: 'm1-8', pole: 'b', text: 'Run the pilot rollout and make it stick in a real team' },
];

// ---------------------------------------------------------------------------
// M2 — the mindset sliders
// ---------------------------------------------------------------------------

/**
 * Four situations, each with two REASONABLE instincts — the failure mode of a mindset quiz is one
 * obviously-correct end, so both anchors here are things a good consultant genuinely does.
 *
 * `scoredSide` is 'left' on every question because `deriveArchetype` reads the raw slider value (0–2 =
 * Strategy). A right-scored question would need the engine to invert the value, which it does not do,
 * so the Strategy anchor must always sit on the left.
 */
const M2_QUESTIONS: readonly LabsMindsetQuestion[] = [
  {
    id: 'q1',
    leftLabel: 'Interview the leadership team',
    q: "A client's profit is shrinking and nobody agrees why. What's your first move?",
    rightLabel: 'Go watch the work being done',
    scoredSide: 'left',
  },
  {
    id: 'q2',
    leftLabel: 'The market analysis nobody asked for',
    q: 'You have one free week and nobody has asked you for anything. What do you build?',
    rightLabel: 'The fix for the process everyone complains about',
    scoredSide: 'left',
  },
  {
    id: 'q3',
    leftLabel: 'Question the plan',
    q: "The new plan is six weeks in and the numbers haven't moved.",
    rightLabel: 'Question the execution',
    scoredSide: 'left',
  },
  {
    // AI now drafts a competent strategy deck in seconds, which is squarely the automatable half of
    // this job. What it cannot do is judge whether the answer is right for THIS business, or get an
    // organisation to actually change. Asking the learner where their value sits is the live question
    // in the field, and it splits the poles honestly rather than flattering either.
    id: 'q4',
    leftLabel: 'Knowing which answer is right',
    q: 'An AI drafts the strategy deck in four seconds. Where does your value sit?',
    rightLabel: 'Getting the org to act on it',
    scoredSide: 'left',
  },
];

// ---------------------------------------------------------------------------
// M3 — the practice subject (Swiftcart) + the three rates
// ---------------------------------------------------------------------------

/**
 * Swiftcart — the fictional practice business (finance's NovaTech, data's PulseApp). Fictional ON
 * PURPOSE: the numbers are built to teach ONE lesson cleanly, which no real company's table does.
 *
 * The lesson: the topline more than triples (+217%) while EVERY quality gauge decays in lockstep —
 * on-time delivery 90.0% → 84.8% → 80.0%, tickets per order 8% → 10% → 12%, refunds 3.0% → 4.0% →
 * 5.0%. The business is not straining under growth; it is being outrun by it. The reveal copy in
 * `consulting-copy.ts` states those percentages literally, so these cells and that paragraph move
 * together.
 */
const M3_SUBJECT: LabsSubject = {
  accent: CONSULTING_AXIS.poles.b.accent,
  id: 'swiftcart',
  logo: '',
  monogram: 'S',
  name: 'Swiftcart',
  periods: ['FY2022', 'FY2023', 'FY2024'],
  rows: [
    { key: 'orders', label: 'Orders', values: [120, 210, 380] },
    { emphasis: 'subtotal', key: 'onTime', label: 'On-Time Deliveries', values: [108, 178, 304] },
    { key: 'tickets', label: 'Support Tickets', values: [9.6, 21, 45.6] },
    { emphasis: 'subtotal', key: 'refunds', label: 'Refunds', values: [3.6, 8.4, 19] },
  ],
  subtitle: '',
  tagline: 'Practice example',
  tags: [],
  unit: { prefix: '', suffix: 'k' },
};

/**
 * The three practice rates. Each wrong option is a REAL mis-derivation a beginner makes, not a random
 * near-miss — dividing by the wrong base, reading the wrong year's column, or computing a different
 * row's rate by mistake. Options are stored canonically; the engine shuffles their positions.
 */
const M3_METRICS: readonly LabsDerivedMetric[] = [
  {
    correct: 216.7,
    formula: '(380 − 120) ÷ 120 × 100',
    key: 'orderGrowth',
    name: 'Order Growth',
    // 81.0 = the FY2024-only leg (380 ÷ 210); 68.4 = dividing the change by the END year, not the base.
    options: [68.4, 81.0, 216.7],
    period: '(2 year)',
    sources: [
      { periodIndex: 0, row: 'orders' },
      { periodIndex: 2, row: 'orders' },
    ],
  },
  {
    correct: 80.0,
    formula: '304 ÷ 380 × 100',
    key: 'onTimeRate',
    name: 'On-Time Rate',
    // 90.0 = FY2022's rate (right formula, wrong column); 84.8 = FY2023's (same mistake, other column).
    options: [80.0, 84.8, 90.0],
    period: '(FY2024)',
    sources: [
      { periodIndex: 2, row: 'onTime' },
      { periodIndex: 2, row: 'orders' },
    ],
  },
  {
    correct: 5.0,
    formula: '19 ÷ 380 × 100',
    key: 'refundRate',
    name: 'Refund Rate',
    // 3.0 = FY2022's refund rate (wrong column); 12.0 = the TICKETS rate (wrong numerator row).
    options: [3.0, 5.0, 12.0],
    period: '(FY2024)',
    sources: [
      { periodIndex: 2, row: 'refunds' },
      { periodIndex: 2, row: 'orders' },
    ],
  },
];

/** ±tolerance on a picked rate, so a rounding difference still counts as right (the other packs use the same). */
const METRIC_TOLERANCE = 1.0;

/** Every option on the M3 rate picker is a percentage. */
const METRIC_UNIT_SUFFIX = '%';

// ---------------------------------------------------------------------------
// M4 — the real subjects
// ---------------------------------------------------------------------------

/** The three reported years, shared by all four subjects (Costco's are its own Aug/Sep fiscal years). */
const M4_PERIODS: readonly string[] = ['FY2022', 'FY2023', 'FY2024'];

/** Costco and Uber report their audiences in millions. */
const M4_UNIT_MILLIONS = { prefix: '', suffix: 'M' } as const;

/**
 * The four real businesses. Each table is the company's OWN reported operating metrics — 10-K,
 * SEC-filed press release or shareholder letter — and each supports the same three reads:
 *
 *   growth      → the trend in row 1 (the audience / demand)
 *   efficiency  → the trend in a deeper tier over the tier above it (see the header note: Costco's
 *                 is executive-inside-PAID members, row 3 over row 2)
 *   risk/opp    → interpretive, never graded
 *
 * No logos yet — `logo: ''` falls back to the monogram (same launch state as the data vertical, whose
 * official brand marks were a design fast-follow).
 */
const M4_SUBJECTS: Readonly<Record<string, LabsSubject>> = {
  airbnb: {
    // NOT Airbnb's brand coral (#FF5A5F): every subject's accent is a tile background with white text
    // on it, and white on that coral is unreadable. This is the darkest rose that still reads as
    // Airbnb and still passes contrast.
    accent: '#C0334D',
    id: 'airbnb',
    logo: '',
    monogram: 'A',
    name: 'Airbnb',
    periods: M4_PERIODS,
    // Revenue ÷ GBV — the take rate — is 13.3% → 13.5% → 13.6%: the flattest line in the set, and
    // that flatness IS the read. Airbnb's audience metric (nights) and listings are reported in
    // different units or only as rounded figures, so this is the two-row table Airbnb itself reports
    // cleanly, and it happens to be the one consultants actually argue about.
    rows: [
      { key: 'gbv', label: 'Gross Booking Value', values: [63.2, 73.3, 81.8] },
      { emphasis: 'subtotal', key: 'revenue', label: 'Revenue', values: [8.4, 9.9, 11.1] },
    ],
    subtitle: 'ABNB',
    tagline: 'Marketplace · takes a cut of every booking',
    tags: ['Marketplace', 'Travel', 'Asset-Light'],
    unit: { prefix: '$', suffix: 'B' },
  },
  chipotle: {
    accent: '#A81612',
    id: 'chipotle',
    logo: '',
    monogram: 'C',
    name: 'Chipotle',
    periods: M4_PERIODS,
    // Chipotlane share of the fleet: 17.9% → 23.6% → 28.7%. Openings accelerate (236 → 271 → 304
    // new restaurants) AND the mix shifts toward the higher-throughput format at the same time —
    // expansion and efficiency moving together, which is the operations story consultants hold up
    // as the benchmark.
    rows: [
      { key: 'restaurants', label: 'Total Restaurants', values: [3187, 3437, 3726] },
      { emphasis: 'subtotal', key: 'chipotlanes', label: 'Chipotlane Locations', values: [571, 811, 1068] },
      { key: 'opened', label: 'New Restaurants Opened', values: [236, 271, 304] },
    ],
    subtitle: 'CMG',
    tagline: 'Restaurants · unit expansion · drive-thru mix shift',
    tags: ['Restaurants', 'Unit Economics', 'Format Shift'],
    unit: { prefix: '', suffix: '' },
  },
  costco: {
    accent: '#C8102E',
    id: 'costco',
    logo: '',
    monogram: 'C',
    name: 'Costco',
    periods: M4_PERIODS,
    // Executive members inside paid members: 44.2% → 45.5% → 46.5%. The audience compounds steadily
    // (+7.6% then +7.0%) while a growing share trades UP to the expensive tier — the membership
    // flywheel deepening as it grows.
    rows: [
      { key: 'cardholders', label: 'Total Cardholders', values: [118.9, 127.9, 136.8] },
      { emphasis: 'subtotal', key: 'paid', label: 'Paid Members', values: [65.8, 71.0, 76.2] },
      { emphasis: 'subtotal', key: 'exec', label: 'Executive Members', values: [29.1, 32.3, 35.4] },
    ],
    subtitle: 'COST',
    tagline: 'Membership retail · the fee is the business',
    tags: ['Retail', 'Membership', 'Loyalty Flywheel'],
    unit: M4_UNIT_MILLIONS,
  },
  uber: {
    // Uber's brand is black; near-black keeps the tile from reading as a rendering error while white
    // text still passes contrast trivially.
    accent: '#141414',
    id: 'uber',
    logo: '',
    monogram: 'U',
    name: 'Uber',
    periods: M4_PERIODS,
    // Trips per consumer: 58.3 → 63.0 → 65.9 a year. The audience grows steadily AND each consumer
    // uses the platform more — frequency, not headcount, is where the operating leverage lives.
    // MAPCs are Q4 monthly averages; trips are the full year's.
    rows: [
      { key: 'mapcs', label: 'Monthly Active Consumers (Q4)', values: [131, 150, 171] },
      { emphasis: 'subtotal', key: 'trips', label: 'Trips (Full Year)', values: [7642, 9448, 11_273] },
    ],
    subtitle: 'UBER',
    tagline: 'Mobility + delivery · frequency is the flywheel',
    tags: ['Marketplace', 'Two-Sided', 'Frequency'],
    unit: M4_UNIT_MILLIONS,
  },
};

const M4_GROUPS: readonly LabsChipGroup[] = [
  {
    chips: [
      { id: 'accel', label: 'Accelerating growth' },
      { id: 'decel', label: 'Decelerating growth' },
      { id: 'steady', label: 'Steady / stable' },
      { id: 'volatile', label: 'Volatile / mixed signals' },
    ],
    graded: true,
    id: 'growth',
    label: 'Growth story',
  },
  {
    // The consulting analogue of data's "engagement depth": is the business getting BETTER at its own
    // job as it grows — a deeper tier gaining share of the tier above it — or just bigger?
    chips: [
      { id: 'improving', label: 'Efficiency improving' },
      { id: 'declining', label: 'Efficiency declining' },
      { id: 'stable', label: 'Efficiency stable' },
      { id: 'mixed', label: 'Mixed across years' },
    ],
    graded: true,
    id: 'efficiency',
    label: 'Operating efficiency',
  },
  {
    // Interpretive: a defensible case exists for every chip here, so the engine never grades it red.
    chips: [
      { id: 'expansion', label: 'Room to expand' },
      { id: 'saturation', label: 'Market saturation' },
      { id: 'efficiency', label: 'Efficiency upside' },
      { id: 'quality', label: 'Growth outrunning quality' },
    ],
    graded: false,
    id: 'riskOpp',
    label: 'Biggest risk or opportunity',
  },
];

/**
 * The intended read of each subject — every answer is derivable from the table above, and nothing else.
 *
 *   airbnb   → GBV +16.0% then +11.6% (decelerating); take rate 13.3 → 13.5 → 13.6% (stable — the
 *              flattest line in the set); growth landing in maturing markets → saturation.
 *   chipotle → restaurants +7.8% then +8.4%, openings 236 → 271 → 304 (accelerating); Chipotlane
 *              share 17.9 → 23.6 → 28.7% (improving); a stated multi-thousand-store runway → expansion.
 *   costco   → cardholders +7.6% then +7.0% (steady); executive share of paid 44.2 → 45.5 → 46.5%
 *              (improving); the model compounds wherever it opens → expansion.
 *   uber     → MAPCs +14.5% then +14.0% (steady); trips per consumer 58.3 → 63.0 → 65.9 (improving);
 *              frequency deepening is the margin lever → efficiency.
 */
const M4_ANSWER_KEY: Readonly<Record<string, LabsAnswerKey>> = {
  airbnb: { efficiency: 'stable', growth: 'decel', riskOpp: 'saturation' },
  chipotle: { efficiency: 'improving', growth: 'accel', riskOpp: 'expansion' },
  costco: { efficiency: 'improving', growth: 'steady', riskOpp: 'expansion' },
  uber: { efficiency: 'improving', growth: 'steady', riskOpp: 'efficiency' },
};

/** Curated 3-bullet reads — editorial, not a live model. Same length and register as the other packs'. */
const M4_SAMPLE_ANALYSES: Readonly<Record<string, string>> = {
  airbnb:
    '• Growth is decelerating: Gross Booking Value went $63.2B → $73.3B → $81.8B, which is +16% then +12%. Still enormous in absolute terms — $8.5B of new bookings in the latest year — but the second leg is clearly slower, and that is the shape of a marketplace whose core markets are maturing.\n\n' +
    '• The take rate is the line to stare at. Revenue as a share of GBV went 13.3% → 13.5% → 13.6% — essentially flat, on purpose. Airbnb grew revenue 32% over two years almost entirely by growing the marketplace, not by squeezing a bigger cut from each booking. That restraint is a strategic choice: the take rate is the price hosts pay, and raising it invites the competition in.\n\n' +
    '• Risk: when GBV growth slows and the take rate is deliberately flat, revenue growth has only one engine, and it is decelerating. Opportunity: that untouched take rate IS the stored value — a single point of take rate is roughly $800M of high-margin revenue, available whenever Airbnb decides the marketplace can bear it.',

  chipotle:
    '• Growth is genuinely accelerating, which is rare for a 3,000-store chain: the fleet went 3,187 → 3,437 → 3,726, and the OPENINGS line is the tell — 236, then 271, then 304 new restaurants a year. Most chains this size are slowing down; this one is speeding up.\n\n' +
    '• The mix shift is the smarter story. Chipotlane locations went 571 → 811 → 1,068, so the drive-thru share of the fleet went 17.9% → 23.6% → 28.7%. The company is not just adding stores, it is adding a HIGHER-THROUGHPUT format faster than the base grows — expansion and operating improvement in the same motion, which is what a well-run rollout looks like.\n\n' +
    '• Risk: an accelerating build-out is also an accelerating bet — every new store assumes the demand keeps showing up, and construction commits years ahead of it. Opportunity: at 3,726 company-owned stores against a stated runway several times that in North America alone, the constraint is execution, not market — which is exactly the constraint an operator wants.',

  costco:
    '• Growth is steady to the point of being boring, and that is the highest compliment this table can pay: cardholders went 118.9M → 127.9M → 136.8M, +7.6% then +7.0%. No spike, no slump — a membership machine adding roughly 9 million cards a year, every year.\n\n' +
    '• The real story is the trade-up. Executive members — the expensive tier — went 29.1M → 32.3M → 35.4M, which is 44.2% → 45.5% → 46.5% of all paid members. The base grows AND a rising share of it chooses to pay more. That is the flywheel deepening as it spins, and it is why the membership fee, not the merchandise margin, is the business.\n\n' +
    "• Risk: a model this dependent on renewal has one existential number — the renewal rate — and it lives outside this table, so this read is incomplete by construction. Opportunity: the model compounds wherever a warehouse opens, and most of the world doesn't have one yet.",

  uber:
    '• Growth is steady: Monthly Active Consumers went 131M → 150M → 171M, +14.5% then +14.0% — two nearly identical legs, which for a platform this size is remarkable consistency rather than a slowdown.\n\n' +
    '• Frequency is the story underneath. Trips went 7.6B → 9.4B → 11.3B, so trips PER consumer went 58.3 → 63.0 → 65.9 a year. Each user is worth more trips than the year before — the audience deepens as it widens, and every extra trip rides on infrastructure that is already paid for. That is where the operating leverage in a marketplace actually lives.\n\n' +
    '• Risk: frequency gains this smooth usually mean the same dense urban cohorts riding more, so the read flatters the core while saying nothing about the next 100M consumers, who live in thinner markets. Opportunity: if membership and multi-product habit keep compounding frequency, Uber grows revenue without acquiring a single new user — the cheapest growth there is.',
};

/** Sample push-backs, auto-filled on reveal. `_default` backs any subject with no entry. */
const M4_TAKE_SAMPLES: Readonly<Record<string, string>> = {
  _default:
    "I agree with the core read, but I'd push back on how lightly the analysis treats the weakest gauge in this table. That's where the real debate is.",
  airbnb:
    "I agree the take rate is the headline, but I'd push back on calling its flatness pure restraint. It may mean Airbnb has already tested the ceiling and the marketplace pushed back — which makes the 'stored value' smaller than it looks.",
  chipotle:
    "I agree the Chipotlane shift is the story, but I'd flag that the share gain comes heavily from NEW stores. The number that matters is whether an added lane lifts an existing store's throughput, and this table can't show it.",
  costco:
    "I agree the trade-up trend is real, but I'd push back on reading it as pure strength: executive members are the customers most exposed to a fee increase, and this table says nothing about how they respond to one.",
  uber: "I agree frequency is the lever, but I'd push back on the smoothness: identical growth legs across two years on a platform this size can also mean the core markets are maturing in sync, and the next leg has to come from somewhere thinner.",
};

// ---------------------------------------------------------------------------
// Recommendations
// ---------------------------------------------------------------------------

/**
 * MEMBERSHIP — the catalog `tags.categories` values that put a program in this vertical at all. Also
 * the gate on this vertical's entry, so the taxonomy can never drift between the two.
 *
 * Every value below is a REAL career-path filter value observed in production (catalog filter events,
 * 30-day audit 2026-08-06) — the filter options are built verbatim from the live catalog's
 * `tags.categories`, so a value seen in a filter exists on at least one live program.
 *
 * DELIBERATELY DISJOINT from both `FINANCE_CATEGORIES` and the data pack's `DATA_CATEGORIES`, for two
 * reasons that are easy to un-learn later:
 *   1. Retirement scoping: `isEnrolledInVertical` retires an entry on enrollment in one of the
 *      vertical's OWN programs. Shared categories would make one enrollment retire two verticals.
 *   2. Modal collision: browsing the catalog by a category lights that vertical's entry. A category
 *      claimed by two engine verticals would light two modals on the same click (the same bug the
 *      finance-yield rule exists to prevent — see `yieldsToPrecedingLabsEntry`).
 * The visible cost: the VC/deal-sourcing programs (Mangusta, HP Tech Ventures, EIC) stay FINANCE's
 * even though "strategy" learners often want them, and People Analytics stays DATA's even though
 * Amazon's operational-strategy program carries it. Both are the right call — the yield/retirement
 * machinery needs one owner per category.
 *
 * ⚠️ AUDIT BEFORE FLIPPING THE FLAG (the QA-433 lesson): confirm against the LIVE PROD catalog which
 * programs these five categories actually match, and that the pool is non-trivial. The category
 * strings are confirmed real; the per-program tagging is ops-owned and can drift.
 */
const CONSULTING_CATEGORIES: readonly string[] = [
  'Strategy & Consulting',
  'Business Development',
  'Process & Workflow Design',
  'Market Research',
  'Consumer Insights',
];

/**
 * ORDER — which of the vertical's programs suit each archetype. Matched against each program's
 * categories PLUS its `skills` and `topics` (see `LabsRecommendationConfig`), and — per the data
 * pack's hard-won rule — none of these tags is a MEMBERSHIP category above, so an ops re-tag cannot
 * silently inflate a tier.
 *
 *   strategist → the advisory half: research, competitive analysis, the deck, the room.
 *                ('Survey Design' is confirmed real on Beats' consumer-research program.)
 *   operator   → the execution half: process, workforce, root cause.
 *                ('Workforce Data Analysis' is confirmed real on Amazon's operational-strategy
 *                 program.)
 *
 * `gen` keeps no priority: THE POLYMATH is the learner both halves suit, so the whole pool qualifies
 * equally and it sorts on soonest start.
 *
 * ⚠️ Same empirical audit as membership: run each list against the live catalog and confirm the tiers
 * are of comparable, non-trivial size (see `registry.spec.ts` on why no unit test can do this).
 */
const ARCHETYPE_PRIORITY: Readonly<Record<string, readonly string[]>> = {
  gen: [],
  operator: ['Operations Management', 'Process Improvement', 'Root Cause Analysis', 'Workforce Data Analysis'],
  strategist: [
    'Competitive Analysis',
    'Corporate Strategy',
    'Presentation Skills',
    'Strategic Communication',
    'Survey Design',
  ],
};

/**
 * Static fallback cards, used ONLY when the live catalog call fails or returns nothing. Programs are
 * real Extern programs; no consulting-vertical art exists yet, so the images are the generic
 * per-position placeholders and the logos are empty (the card falls back to the company name).
 * `duration` is the one field here not sourced from the catalog — treat it as provisional.
 */
const REC_IMAGES = {
  best: '/images/rec/best-match.png',
  cards: ['/images/rec/card-1.png', '/images/rec/card-2.png'],
} as const;

/**
 * Every card below names a REAL, ACTIVE program (all four appear in the last 30 days of production
 * registrations, audited 2026-08-06). Amazon's and Beats' titles and durations match the data pack's
 * catalog-verified cards for the same programs. A fallback card is shown precisely when the live
 * catalog is DOWN, i.e. when nobody can click through and discover the program doesn't exist, so a
 * fabricated card would be a mis-sale with no safety net.
 */
const RECOMMENDATIONS: Readonly<Record<string, readonly LabsRecommendation[]>> = {
  // THE POLYMATH — both halves: one operational, one advisory, one that blends them.
  gen: [
    {
      blurb:
        'The whole consulting loop in one engagement: diagnose a real healthcare operation, find where it leaks time and money, and hand the team a recommendation they can run.',
      company: 'Extern',
      duration: '8 weeks',
      image: REC_IMAGES.best,
      logo: '',
      tags: ['Healthcare Operations', 'Consulting', 'Process Improvement'],
      title: 'Healthcare Operations & Consulting',
      whatYoullLearn: [
        'Map a real operation end to end',
        'Find the constraint that actually binds',
        'Turn a diagnosis into a recommendation',
        'Defend it to people who run the operation',
      ],
    },
    {
      blurb:
        'Strategy with evidence at one of the biggest operators on earth: analyze real workforce data and take a root-cause finding to an operations team.',
      company: 'Amazon',
      duration: '8 weeks',
      image: REC_IMAGES.cards[0],
      logo: '',
      tags: ['Operational Strategy', 'People Analytics', 'Root Cause Analysis'],
      title: 'Operational Strategy & People Analytics',
      whatYoullLearn: [
        'Work a real operational problem end to end',
        'Segment a real workforce',
        'Find the root cause behind an operational metric',
        'Brief an operations team on what to do next',
      ],
    },
    {
      blurb:
        'The advisory half at a brand you know: reconcile what customers say with what they do, and hand the product team a decision, not a deck.',
      company: 'Beats by Dre',
      duration: '8 weeks',
      image: REC_IMAGES.cards[1],
      logo: '',
      tags: ['Market Research', 'Consumer Insights', 'Survey Design'],
      title: 'Consumer Behavior & Market Analysis',
      whatYoullLearn: [
        'Design a survey that produces usable data',
        'Run a competitor and consumer analysis',
        'Turn an insight into a recommendation',
        'Brief a brand team on what to do next',
      ],
    },
  ],
  // THE OPERATOR — the execution half: process, workforce, the fix that holds.
  operator: [
    {
      blurb:
        'Operations at scale: scrape real employee-voice data, run the analysis, and find the root cause behind an operational problem a team actually has.',
      company: 'Amazon',
      duration: '8 weeks',
      image: REC_IMAGES.best,
      logo: '',
      tags: ['Operational Strategy', 'Workforce Data Analysis', 'Root Cause Analysis'],
      title: 'Operational Strategy & People Analytics',
      whatYoullLearn: [
        'Collect and clean real workforce data',
        'Segment a real workforce',
        'Find the root cause behind an operational metric',
        'Take the finding to an operations team',
      ],
    },
    {
      blurb:
        'A real operation with real stakes: find where a healthcare workflow loses time and money, and design the fix the team can actually run.',
      company: 'Extern',
      duration: '8 weeks',
      image: REC_IMAGES.cards[0],
      logo: '',
      tags: ['Healthcare Operations', 'Process Improvement', 'Consulting'],
      title: 'Healthcare Operations & Consulting',
      whatYoullLearn: [
        'Map a process and find where it breaks',
        'Quantify what the breakage costs',
        'Design a fix that survives a real team',
        'Present it to the people who own the process',
      ],
    },
    {
      blurb:
        'Commerce operations end to end: analyze a real e-commerce funnel and turn what the data says into a merchandising strategy the business can act on.',
      company: 'Breaking Games',
      duration: '8 weeks',
      image: REC_IMAGES.cards[1],
      logo: '',
      tags: ['E-Commerce', 'Data Analysis', 'Strategy'],
      title: 'E-Commerce Data Analysis & Strategy',
      whatYoullLearn: [
        'Read a real e-commerce funnel',
        'Find the operational drag on conversion',
        'Turn the analysis into a strategy',
        'Recommend a merchandising change',
      ],
    },
  ],
  // THE STRATEGIST — the advisory half: research, insight, the recommendation.
  strategist: [
    {
      blurb:
        'Consumer insight at a brand you know — design the research, run the competitive analysis, and turn what you find into a strategy recommendation.',
      company: 'Beats by Dre',
      duration: '8 weeks',
      image: REC_IMAGES.best,
      logo: '',
      tags: ['Market Research', 'Consumer Insights', 'Competitive Analysis'],
      title: 'Consumer Behavior & Market Analysis',
      whatYoullLearn: [
        'Design a survey that produces usable data',
        'Run a competitor and consumer analysis',
        'Turn an insight into a recommendation',
        'Brief a brand team on what to do next',
      ],
    },
    {
      blurb:
        'Strategy grounded in a real operation: diagnose a healthcare business, size what the problems cost, and make the recommendation to the people who decide.',
      company: 'Extern',
      duration: '8 weeks',
      image: REC_IMAGES.cards[0],
      logo: '',
      tags: ['Consulting', 'Healthcare Operations', 'Strategic Communication'],
      title: 'Healthcare Operations & Consulting',
      whatYoullLearn: [
        'Diagnose a real operation',
        'Size a problem in time and money',
        'Build the recommendation deck',
        'Defend it in front of the decision-makers',
      ],
    },
    {
      blurb:
        'Evidence-first strategy at scale: analyze real workforce data at Amazon and turn a root-cause finding into a recommendation an operations leader will act on.',
      company: 'Amazon',
      duration: '8 weeks',
      image: REC_IMAGES.cards[1],
      logo: '',
      tags: ['Operational Strategy', 'People Analytics', 'Strategic Communication'],
      title: 'Operational Strategy & People Analytics',
      whatYoullLearn: [
        'Work with real workforce data',
        'Find the finding that changes the decision',
        'Build the case for a change',
        'Brief a leadership audience',
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// The pack
// ---------------------------------------------------------------------------

export const CONSULTING_PACK: VerticalPack = {
  archetypes: CONSULTING_ARCHETYPES,
  axis: CONSULTING_AXIS,
  // Must be verbatim one of the application's career-focus options, or the entry never shows to anyone.
  careerFocusArea: 'Strategy & Consulting',
  copy: CONSULTING_COPY,
  deriveArchetype,
  displayName: 'Consulting & Strategy',
  events: CONSULTING_DISCOVERY_EVENTS,
  featureFlag: 'externship_labs_consulting_q3_2026',
  m1: { cards: M1_CARDS },
  m2: { questions: M2_QUESTIONS },
  // Mirrors the other rails ("Finance Sprint" / "Data Sprint"): the rail names the VERTICAL, never one
  // of its poles. This previously read "Strategy Sprint" / "Strategy", which named the advisory pole —
  // so a learner who came out as THE OPERATOR, the execution pole, returned to a nav item named after
  // the half they are not. Finance and data have no such trap because neither axis pole shares a name
  // with its vertical. Compass is the door glyph too.
  nav: { icon: Compass, label: 'Consulting Sprint', mobileLabel: 'Consulting' },
  m3: {
    metrics: M3_METRICS,
    subject: M3_SUBJECT,
    tolerance: METRIC_TOLERANCE,
    unitSuffix: METRIC_UNIT_SUFFIX,
  },
  m4: {
    answerKey: M4_ANSWER_KEY,
    groups: M4_GROUPS,
    sampleAnalyses: M4_SAMPLE_ANALYSES,
    subjects: M4_SUBJECTS,
    takeSamples: M4_TAKE_SAMPLES,
  },
  recommendations: {
    archetypePriority: ARCHETYPE_PRIORITY,
    categories: CONSULTING_CATEGORIES,
    fallback: RECOMMENDATIONS,
  },
  slug: 'consulting',
};
